import { z } from "zod";
import { zfd } from "zod-form-data";
import { phoneFormats, validatePhoneNumber } from "../utils/phoneFormats";

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

export const emailSchema = z
  .string()
  .email({ message: "Invalid Email address" });

/**
 * Validates phone number based on country code
 * @param {string} phoneNumber - The formatted phone number
 * @param {string} countryCode - ISO country code (e.g., 'US', 'GB', 'IN')
 * @param {boolean} isRequired - Whether the phone number is required (default: false)
 * @returns {boolean} - Whether the phone number is valid for the country
 */
export const validatePhoneForCountry = (phoneNumber, countryCode = 'US', isRequired = false) => {
  // If required and empty, return false
  if (isRequired && (!phoneNumber || !phoneNumber.trim())) {
    return false;
  }
  
  // If not required and empty, return true
  if (!isRequired && (!phoneNumber || !phoneNumber.trim())) {
    return true;
  }
  
  // Use the comprehensive validation function from phoneFormats
  const result = validatePhoneNumber(phoneNumber, countryCode);
  
  return result.isValid;
};

/**
 * Gets the validation example for a specific country
 * @param {string} countryCode - ISO country code
 * @returns {string} - Example phone number for the country
 */
export const getPhoneValidationExample = (countryCode = 'US') => {
  const phoneFormat = phoneFormats[countryCode] || phoneFormats['US'];
  return phoneFormat.placeholder;
};

/**
 * Gets the required phone number length for a specific country
 * @param {string} countryCode - ISO country code
 * @returns {number} - Required number of digits
 */
export const getPhoneLength = (countryCode = 'US') => {
  const phoneFormat = phoneFormats[countryCode] || phoneFormats['US'];
  return phoneFormat.maxLength;
};

/**
 * Gets the validation error message for a phone number
 * @param {string} phoneNumber - The formatted phone number
 * @param {string} countryCode - ISO country code (e.g., 'US', 'GB', 'IN')
 * @returns {string|null} - Error message or null if valid
 */
export const getPhoneValidationError = (phoneNumber, countryCode = 'US') => {
  const result = validatePhoneNumber(phoneNumber, countryCode);
  return result.error;
};

/**
 * Phone number validation schema with country-specific rules
 * @param {boolean} isOptional - Whether the phone number is optional
 * @param {string} countryCode - ISO country code for validation (default: 'US')
 * @returns {z.ZodString} - Zod validation schema
 */
export const phoneSchema = (isOptional = false, countryCode = 'US') => {
  const baseSchema = z.string();
  const phoneFormat = phoneFormats[countryCode] || phoneFormats['US'];

  return isOptional
    ? baseSchema
        .optional()
        .refine(
          (val) => !val || validatePhoneForCountry(val, countryCode, false),
          (val) => {
            if (!val) return { message: '' };
            const error = getPhoneValidationError(val, countryCode);
            return {
              message: error || `Invalid phone number. Expected format: ${phoneFormat.placeholder}`
            };
          }
        )
    : baseSchema
        .min(1, "Phone number is required")
        .refine(
          (val) => validatePhoneForCountry(val, countryCode, true),
          (val) => {
            const error = getPhoneValidationError(val, countryCode);
            return {
              message: error || `Invalid phone number. Expected format: ${phoneFormat.placeholder}`
            };
          }
        );
};

export const ImageSchema = (isOptional = false) => {
  const baseSchema = zfd
    .file()
    .refine((file) => file.size < MAX_FILE_SIZE, {
      message: "File can't be bigger than 5MB.",
    })
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file.type),
      {
        message: "Please use a JPG or PNG image.",
      },
    );

  return isOptional ? baseSchema.optional() : baseSchema;
};
